import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `shell` — the room the product happens in, and every word painted on it.
 *
 * The rail, the top bar, the command palette, the auth gate and the screen a page shows when
 * it fails while rendering. One namespace rather than five, because they are one surface to
 * a reader: the palette lists the same destinations the rail does, and somebody who reads a
 * word on the rail and types it into the palette has to land on the row carrying that word.
 * Two namespaces would let those two drift, which is the drift `common` exists to prevent.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * Anything that interpolates is a FUNCTION returning a whole sentence, never a template
 * assembled at the call site (LOC-013) — a translator handed 'Pin ' and a title cannot
 * produce a language that puts the verb somewhere else.
 */

/**
 * The slice of `useI18n()` a counting entry here needs, threaded in from the component.
 *
 * Structural rather than `Pick<I18n, …>`: this file is imported by `messages/en/index.ts`,
 * which `I18nProvider` imports in turn, so naming `I18n` here would close that circle for no
 * gain. What a counting sentence actually needs is a locale and a number formatter.
 */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/**
 * Picks the form CLDR says this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese
 * takes the singular at zero and a two-branch comparison is already wrong in one of the two
 * shipped languages, on every empty state. `other` is required and is the fallback, so a
 * locale with `few`/`many` can add them here without touching a call site.
 */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const shell = {
  // --- the rail, and the drawer it becomes on a phone ----------------------
  /** The navigation landmark's accessible name. Both browser suites use it as the signed-in signal. */
  rail: 'Workspace',
  openMenu: 'Open the workspace menu',
  closeMenu: 'Close the workspace menu',
  /**
   * The wordmark's accessible name. The deployment's own name is a proper noun and never
   * translates (D-17), so it arrives as a parameter and only the sentence around it moves.
   */
  homeLink: (productName: string): string => `${productName} — go to the home screen`,
  /**
   * The badge beside the wordmark. An infrastructure term of art, left in English on purpose
   * (D-15): a Brazilian operator reads "on-prem" in every tool they run, and it sits in a
   * 9px uppercase pill where a translation would not fit anyway.
   */
  onPrem: 'on-prem',
  onPremTitle: 'This deployment runs inside your own network.',

  newConversation: 'New conversation',
  newConversationTitle: 'Start a new conversation',

  // --- the conversation list ----------------------------------------------
  loadingConversations: 'Loading your conversations…',
  noConversations: 'Nothing here yet. Start a conversation to see it listed.',
  /**
   * A conversation's title is what its reader wrote and is never translated (D-17). These
   * take it whole so the verb can go where each language wants it, rather than a word being
   * prefixed to a name at the call site.
   */
  pinConversation: (title: string): string => `Pin ${title}`,
  unpinConversation: (title: string): string => `Unpin ${title}`,
  deleteConversation: (title: string): string => `Delete ${title}`,

  /**
   * The recency headings over the rail, keyed by `ConversationGroup['key']`.
   *
   * `conversation-groups.ts` used to carry these words itself. It no longer does, and the
   * display field is gone from its type rather than merely unused (the shape of PRD-27 F-9):
   * a pure module that returns its own labels has to be handed a catalogue to stay
   * switchable, and the key it already computes says everything a heading needs.
   *
   * Portuguese agrees "Pinned" with *conversa* (f.), so these are adjectives there rather
   * than nouns and cannot be shared with any other surface's idea of the same word.
   */
  conversationGroups: {
    pinned: 'Pinned',
    today: 'Today',
    yesterday: 'Yesterday',
    week: 'This week',
    earlier: 'Earlier'
  },

  /**
   * The destinations, named once.
   *
   * The rail and the command palette both render these and must agree: somebody who reads
   * "Knowledge" on the rail and types it into the palette has to find the row that says it.
   * Two copies would be two translations, and the palette would stop finding the rail.
   */
  nav: {
    agents: 'Agents',
    knowledge: 'Knowledge',
    memory: 'Memory',
    groups: 'Groups',
    activity: 'Activity',
    governance: 'Governance',
    people: 'People'
  },

  // --- what is waiting, as a screen reader hears it ------------------------
  approvalsWaiting: (fmt: Counting, count: number): string =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} approval waiting`,
      other: `${fmt.formatCount(count)} approvals waiting`
    }),
  sharingWaiting: (fmt: Counting, count: number): string =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} sharing request waiting`,
      other: `${fmt.formatCount(count)} sharing requests waiting`
    }),

  // --- the account card at the foot of the rail ----------------------------
  useLightTheme: 'Use light theme',
  useDarkTheme: 'Use dark theme',
  signOut: 'Sign out',

  /**
   * The command palette. It navigates and nothing else, so every word here is about finding
   * rather than doing.
   */
  palette: {
    title: 'Search the workspace',
    /**
     * One sentence in four places: the header button's visible label, its screen-reader-only
     * twin below `sm`, and the palette input's own placeholder and accessible name. They are
     * the same promise, so they are one entry rather than four that can drift apart.
     */
    placeholder: 'Search conversations, agents, knowledge, groups',
    close: 'Close search',
    results: 'Results',
    looking: 'Looking…',
    noMatches: 'Nothing matched that.',
    /**
     * The heading over each rail of results, keyed by `PaletteGroupId` — never by the
     * heading itself, which is what used to key the icon map too (F-9).
     */
    groups: {
      conversations: 'Conversations',
      agents: 'Agents',
      knowledge: 'Knowledge',
      groups: 'Groups',
      destinations: 'Go to'
    },
    /** Home is also the answer to "start a conversation": `/` is where one is composed. */
    home: 'Home',
    homeDetail: 'Start a conversation',
    yourAgents: 'Your agents'
  },

  /** The auth gate, while the session resolves. The spinner is decoration; this is the announcement. */
  checkingSession: 'Checking your session…',

  /** A page that failed while rendering (D-66): usually the app and the API one release apart. */
  routeError: {
    title: 'This page didn’t load',
    body: 'Reloading usually fixes it, especially right after an update. If it keeps happening, let an administrator know.',
    reload: 'Reload',
    home: 'Go to the home screen',
    details: 'Details for support',
    /**
     * `describeRouteError` takes this rather than owning it. It is the one word in that pure
     * module that a person reads, and prose lives here with all the other prose (LOC-012).
     */
    unknown: 'Unknown error'
  }
}
